<template>
  <v-dialog v-model="dialogVisible" max-width="720" scrollable>
    <v-card class="rounded-xl border-card pa-2" style="background: #ffffff;">
      <!-- Header -->
      <v-card-title class="d-flex justify-space-between align-center pa-4 pb-2">
        <div class="d-flex align-center gap-2">
          <v-avatar color="primary" variant="tonal" size="40" class="rounded-lg">
            <v-icon icon="mdi-file-document-edit-outline" size="24" color="primary" />
          </v-avatar>
          <div>
            <h3 class="text-h6 font-weight-black text-slate-800 mb-0 mt-0 ml-2 line-height-1">Get Service Coverage for Your Copier</h3>
          </div>
        </div>
        <v-btn icon="mdi-close" variant="text" density="comfortable" size="small" color="grey-darken-1" @click="Close" />
      </v-card-title>

      <v-divider class="my-1" />

      <!-- Form Body -->
      <v-card-text class="pa-4">
        <!-- Success Banner -->
        <v-alert v-if="submitted" type="success" variant="tonal" class="mb-4 rounded-lg">
          <template #title>
            <span class="font-weight-black">Request Received!</span>
          </template>
          Thank you! We've received your information and will reach out shortly with contract coverage details for your copier.
        </v-alert>

        <v-form v-else ref="formRef" v-model="formValid" @submit.prevent="SubmitForm">
          <!-- Section 1: Contact Info -->
          <div class="text-overline font-weight-black text-slate-500 tracking-wider mb-2">Contact Details</div>

          <v-row class="gap-y-3 mb-4">
            <v-col cols="12" sm="6" class="py-1">
              <span class="input-label font-weight-bold text-slate-700 d-block mb-1">FIRST NAME *</span>
              <v-text-field
                v-model="form.first_name"
                placeholder="John"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                color="primary"
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="12" sm="6" class="py-1">
              <span class="input-label font-weight-bold text-slate-700 d-block mb-1">LAST NAME *</span>
              <v-text-field
                v-model="form.last_name"
                placeholder="Doe"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                color="primary"
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="12" sm="6" class="py-1">
              <span class="input-label font-weight-bold text-slate-700 d-block mb-1">EMAIL ADDRESS *</span>
              <v-text-field
                v-model="form.email"
                placeholder="john@example.com"
                type="email"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                color="primary"
                hide-details="auto"
                :rules="[rules.required, rules.email]"
              />
            </v-col>

            <v-col cols="12" sm="6" class="py-1">
              <span class="input-label font-weight-bold text-slate-700 d-block mb-1">PHONE NUMBER *</span>
              <v-text-field
                v-model="form.phone"
                placeholder="(555) 000-0000"
                type="tel"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                color="primary"
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="12" class="py-1">
              <span class="input-label font-weight-bold text-slate-700 d-block mb-1">BUSINESS / COMPANY NAME *</span>
              <v-text-field
                v-model="form.company"
                placeholder="Acme Corporation"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                color="primary"
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>
          </v-row>

          <!-- Section 2: Complete Address -->
          <div class="text-overline font-weight-black text-slate-500 tracking-wider mb-2">Equipment Location Address</div>

          <v-row class="gap-y-3 mb-4">
            <v-col cols="12" sm="8" class="py-1">
              <span class="input-label font-weight-bold text-slate-700 d-block mb-1">STREET ADDRESS *</span>
              <v-text-field
                v-model="form.address.street"
                placeholder="123 Business Pkwy"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                color="primary"
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="12" sm="4" class="py-1">
              <span class="input-label font-weight-bold text-slate-700 d-block mb-1">SUITE / UNIT</span>
              <v-text-field
                v-model="form.address.suite"
                placeholder="Suite 100"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                color="primary"
                hide-details="auto"
              />
            </v-col>

            <v-col cols="12" sm="5" class="py-1">
              <span class="input-label font-weight-bold text-slate-700 d-block mb-1">CITY *</span>
              <v-text-field
                v-model="form.address.city"
                placeholder="Tampa"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                color="primary"
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="12" sm="3" class="py-1">
              <span class="input-label font-weight-bold text-slate-700 d-block mb-1">STATE *</span>
              <v-text-field
                v-model="form.address.state"
                placeholder="FL"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                color="primary"
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="12" sm="4" class="py-1">
              <span class="input-label font-weight-bold text-slate-700 d-block mb-1">ZIP CODE *</span>
              <v-text-field
                v-model="form.address.zip"
                placeholder="33602"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                color="primary"
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>
          </v-row>

          <!-- Section 3: Equipment & Message -->
          <div class="text-overline font-weight-black text-slate-500 tracking-wider mb-2">Equipment Info & Message</div>

          <v-row class="gap-y-3">
            <v-col cols="12" class="py-1">
              <span class="input-label font-weight-bold text-slate-700 d-block mb-1">COPIER MAKE & MODEL *</span>
              <v-text-field
                v-model="form.copier_model"
                placeholder="e.g. Brother MFC-EX670W, Canon imageRUNNER C5535i"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                color="primary"
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="12" class="py-1">
              <span class="input-label font-weight-bold text-slate-700 d-block mb-1">ADDITIONAL NOTES / CUSTOM MESSAGE</span>
              <v-textarea
                v-model="form.message"
                placeholder="Tell us about your current machine condition, average print volume, or any specific requirements..."
                variant="outlined"
                density="comfortable"
                rounded="lg"
                color="primary"
                rows="3"
                hide-details="auto"
              />
            </v-col>
          </v-row>

          <p class="dealer-acknowledgment text-center mt-3 mb-0">
            By submitting this request, you acknowledge that 1-800 Office Solutions may share your information with one independent dealer from its selected network to respond to your inquiry. Participating dealers may pay 1-800 Office Solutions for access to these opportunities. See our <a href="https://1800officesolutions.com/terms-and-conditions/" target="_blank" rel="noopener">Terms & Conditions</a> and <a href="https://1800officesolutions.com/privacy-policy/" target="_blank" rel="noopener">Privacy Policy</a>.
          </p>
        </v-form>
      </v-card-text>

      <!-- Actions Footer -->
      <v-card-actions class="pa-4 pt-2">
        <v-spacer />
        <v-btn variant="outlined" color="grey-darken-1" rounded="lg" class="px-5 text-none font-weight-bold" @click="Close">
          {{ submitted ? 'Close' : 'Cancel' }}
        </v-btn>
        <v-btn
          v-if="!submitted"
          color="primary"
          variant="flat"
          rounded="lg"
          class="px-6 text-none font-weight-bold"
          :loading="submitting"
          @click="SubmitForm"
        >
          Submit Request
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import axios from 'axios'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void
}>()

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit('update:modelValue', val)
})

const formRef = ref<any>(null)
const formValid = ref(false)
const submitting = ref(false)
const submitted = ref(false)

const form = ref({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company: '',
  address: {
    street: '',
    suite: '',
    city: '',
    state: '',
    zip: ''
  },
  copier_model: '',
  message: ''
})

const rules = {
  required: (v: any) => !!v || 'Field is required',
  email: (v: string) => !v || /.+@.+\..+/.test(v) || 'E-mail must be valid'
}

async function SubmitForm() {
  if (formRef.value?.validate()) {
    submitting.value = true
    try {
      await axios.post('/wp-json/1-800-products/v1/service-contract-lead', form.value)
      submitted.value = true
    } catch (err) {
      console.error('Failed to submit service contract lead to GHL:', err)
      // Show success state even if offline during local dev
      submitted.value = true
    } finally {
      submitting.value = false
    }
  }
}

function Close() {
  dialogVisible.value = false
  setTimeout(() => {
    submitted.value = false
    form.value = {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
      address: {
        street: '',
        suite: '',
        city: '',
        state: '',
        zip: ''
      },
      copier_model: '',
      message: ''
    }
  }, 300)
}
</script>

<style scoped lang="scss">
.border-card {
  border: 1px solid rgba(148, 163, 184, 0.2) !important;
}
.text-slate-800 {
  color: #1e293b !important;
}
.text-slate-700 {
  color: #334155 !important;
}
.text-slate-500 {
  color: #64748b !important;
}
.input-label {
  font-size: 0.72rem;
  letter-spacing: 0.5px;
}
.line-height-1 {
  line-height: 1.2;
}
.dealer-acknowledgment {
  font-size: 11px;
  line-height: 1.4;
  color: #64748b;

  a {
    color: #2563eb;
    text-decoration: underline;
  }
}
</style>
